import React, { useState } from "react";
import * as Yup from "yup";
import { useFormik } from "formik";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { HiOutlineLockClosed } from "react-icons/hi";
import DefaultInput from "../../components/inputs/defaultInput";
import DefaultButton from "../../components/buttons/defaultButton";
import LOGO from "../../assets/images/hr-logo.png";
import { resetPassword } from "../../../services/workspace/user.service";

const validationSchema = Yup.object().shape({
  password: Yup.string()
    .trim()
    .min(8, "Password must be at least 8 characters")
    .required("Password is required"),
  confirm_password: Yup.string()
    .trim()
    .oneOf([Yup.ref("password")], "Passwords do not match")
    .required("Confirm password is required"),
});

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const token = searchParams.get("token") || "";
  const email = searchParams.get("email") || "";

  const formik = useFormik({
    initialValues: {
      password: "",
      confirm_password: "",
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      if (!token || !email) {
        toast.error("Invalid reset link. Token or email is missing.");
        setSubmitting(false);
        return;
      }

      try {
        await resetPassword({
          token,
          email,
          password: values.password,
          password_confirmation: values.confirm_password,
        });
        toast.success("Password reset successfully. Please login.");
        navigate("/auth/login");
      } catch (error) {
        toast.error(
          error?.response?.data?.message || "Failed to reset password. Please try again."
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <div className="min-h-screen w-full bg-gray-100 flex items-center justify-center p-6">
      <div className="w-full max-w-[28rem] bg-white rounded-xl border border-borderColor p-8">
        <div className="mb-6 flex items-center justify-center">
          <img src={LOGO} alt="Logo" className="h-12" />
        </div>

        <h1 className="text-2xl font-bold text-gray-900">Set New Password</h1>
        <p className="text-gray-600 mt-1 mb-6 text-sm">
          Enter your new password and confirm it to continue.
        </p>

        <form onSubmit={formik.handleSubmit} className="flex flex-col gap-4">
          <DefaultInput
            name="password"
            type={showPassword ? "text" : "password"}
            label="Password"
            placeholder="At least 8 characters"
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            leftIcon={<HiOutlineLockClosed className="w-5 h-5" />}
            classnames="border-gray-200 bg-gray-50"
            error={formik.touched.password && formik.errors.password ? formik.errors.password : ""}
            rightIcon={
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="cursor-pointer text-gray-500 hover:text-gray-700 transition-colors"
              >
                {showPassword ? <AiOutlineEyeInvisible size={20} /> : <AiOutlineEye size={20} />}
              </button>
            }
            required
          />

          <DefaultInput
            name="confirm_password"
            type={showConfirmPassword ? "text" : "password"}
            label="Confirm Password"
            placeholder="Re-enter password"
            value={formik.values.confirm_password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            leftIcon={<HiOutlineLockClosed className="w-5 h-5" />}
            classnames="border-gray-200 bg-gray-50"
            error={
              formik.touched.confirm_password && formik.errors.confirm_password
                ? formik.errors.confirm_password
                : ""
            }
            rightIcon={
              <button
                type="button"
                onClick={() => setShowConfirmPassword((prev) => !prev)}
                className="cursor-pointer text-gray-500 hover:text-gray-700 transition-colors"
              >
                {showConfirmPassword ? (
                  <AiOutlineEyeInvisible size={20} />
                ) : (
                  <AiOutlineEye size={20} />
                )}
              </button>
            }
            required
          />

          <DefaultButton
            type="submit"
            loading={formik.isSubmitting}
            disabled={formik.isSubmitting}
            name={formik.isSubmitting ? "" : "Reset Password"}
          />
        </form>

        <p className="text-center text-sm text-gray-600 mt-6">
          Back to{" "}
          <Link to="/auth/login" className="font-medium text-blue-600 hover:text-blue-700">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
};

export default ResetPassword;
